<template>
  <div class="roles-main">
    <el-card shadow="never" class="filter-card">
      <div class="search">
        <el-date-picker
          v-model="startDate"
          type="datetime"
          placeholder="Start Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD HH:mm:ss"
          size="small"
        />
        <el-date-picker
          v-model="endDate"
          type="datetime"
          placeholder="End Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD HH:mm:ss"
          size="small"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="getRoomStatistics()"
        >
          Search
        </el-button>
      </div>
    </el-card>

    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Rooms by Agent</span>
        <span class="card-badge">Total Rooms: {{ totalCount }}</span>
      </div>
      <div class="chart-user" v-if="list.records.length > 0" id="chart-user">
        <Chart :options="options" />
      </div>
    </el-card>

    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Statistics</span>
      </div>
      <el-table
        v-loading="loading"
        :data="list.records"
        ref="table"
        row-key="id"
        size="small"
        border
        class="beauty-table"
        highlight-current-row
        empty-text="No data"
        :cell-class-name="cellGrayWhite"
      >
        <el-table-column
          prop="nickName"
          label="User"
          align="left"
          min-width="160"
          show-overflow-tooltip
        />
        <el-table-column
          prop="totalCount"
          label="Rooms"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span class="num-cell">{{ scope.row.totalCount }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Avg. Wait Time"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span class="num-cell">{{ formatSeconds(scope.row.avgWaitMatchTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Avg. Chat Time"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span class="num-cell">{{ formatSeconds(scope.row.avgFinishTime) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from '@vue/runtime-core'
import { getRoomsStatisticsByUser } from '../../../../api/report'
import { convertDateTime } from '@/utils/util'
import Chart from '@/components/charts/Charts'
import 'echarts/lib/component/tooltip'
import { cellGrayWhite } from '@/utils/style'
import { ElMessage } from 'element-plus'

const loading = ref(false)

const list = reactive({
  records: [],
})

const startDate = ref(convertDateTime(new Date().setHours(0, 0, 0, 0)))
const endDate = ref(convertDateTime(new Date().setHours(23, 59, 59, 999)))
const totalCount = ref(0)

const options = reactive({
  xAxis: {
    type: 'category',
    data: []
  },
  yAxis: {
    type: 'value'
  },
  series: [
    {
      data: [],
      type: 'bar',
      showBackground: true,
      backgroundStyle: {
        color: 'rgba(180, 180, 180, 0.2)'
      }
    }
  ]
})

async function getRoomStatistics() {
  loading.value = true
  try {
    const { data: res } = await getRoomsStatisticsByUser(
      startDate.value,
      endDate.value
    )
    list.records = res
    const totalCountDatas = []
    const names = []
    if (res.length > 0) {
      totalCount.value = res
        .map(stats => stats.totalCount)
        .reduce((acc, count) => acc + count)
      res.forEach((item, index) => {
        names.push(item.nickName)
        totalCountDatas.push(item.totalCount)
      })
      options.xAxis.data = names;
      options.series[0].data = totalCountDatas;
    } else {
      list.records = []
      options.xAxis.data = []
      options.series[0].data = []
      totalCount.value = 0
    }
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

function formatSeconds(v) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  if (n === 0) return '0s'
  if (n < 60) {
    const s = Math.round(n * 10) / 10
    return (Number.isInteger(s) ? s : s.toFixed(1)) + 's'
  }
  const total = Math.round(n)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  let out = ''
  if (h > 0) out += h + 'h'
  if (m > 0) out += m + 'm'
  if (s > 0) out += s + 's'
  return out || '0s'
}

onMounted(async () => {
  await getRoomStatistics()
})
</script>

<style scoped>
.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.filter-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
}
.search > *:not(h6) + * {
  margin-left: 20px;
}
.section-card {
  border-radius: 10px;
  margin-bottom: 16px;
}
.section-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.card-badge {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  font-variant-numeric: tabular-nums;
}
.beauty-table :deep(thead th) {
  background: #f5f7fa;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.num-cell {
  font-variant-numeric: tabular-nums;
}
.chart-user {
  /*width: 700px;*/
  height: 400px;
  margin: auto;
}
</style>
